import { useEffect, useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { cn } from '../utils/cn';
import type { SwrStatus } from '../services/swrCache';

interface DataFreshnessProps {
  status: SwrStatus;
  /** Re-reads the source, skipping the cached placeholder. */
  onRefresh?: () => void | Promise<void>;
  className?: string;
}

const clock = (ms: number) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/**
 * The freshness badge next to data that comes through the stale-while-revalidate cache:
 * "Cached · refreshing" while the source is re-read, "Live" once it answered, and a red
 * "Cached · refresh failed" when it did not (the cached data stays on screen).
 */
export const DataFreshness = ({ status, onRefresh, className }: DataFreshnessProps) => {
  const [refreshing, setRefreshing] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = useCallback(async () => {
    if (!onRefresh) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  }, [onRefresh]);

  if (status.phase === 'idle' || status.phase === 'error') return null;

  const busy = refreshing || status.phase === 'loading' || status.phase === 'cached';
  let dot = 'bg-glass-400';
  let label: string;
  let title: string | undefined;
  switch (status.phase) {
    case 'loading':
      label = 'Loading…';
      break;
    case 'cached':
      dot = 'bg-yellow-400';
      label = status.cachedAt ? `Cached ${clock(status.cachedAt)} · refreshing` : 'Cached · refreshing';
      title = 'Showing the last copy while Redis is re-read';
      break;
    case 'live':
      dot = 'bg-emerald-400';
      label = status.updatedAt ? `Live · ${clock(status.updatedAt)}` : 'Live';
      title = 'Read from Redis just now';
      break;
    case 'stale':
      dot = 'bg-red-400';
      label = status.cachedAt ? `Cached ${clock(status.cachedAt)} · refresh failed` : 'Cached · refresh failed';
      title = status.error ? `Refresh failed: ${status.error}` : 'Refresh failed';
      break;
    default:
      label = '';
  }

  return (
    <div className={cn('inline-flex items-center gap-1', className)} title={title}>
      <div className="inline-flex items-center gap-1.5 text-xs text-glass-400 tabular-nums whitespace-nowrap" aria-live="polite">
        <div className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', dot, status.phase === 'cached' && 'animate-pulse')} />
        <span>{label}</span>
      </div>
      {onRefresh && (
        <button
          type="button"
          onClick={handleRefresh}
          disabled={busy}
          className="btn-icon btn-icon-sm"
          title="Re-read from Redis"
          aria-label="Re-read from Redis"
        >
          <RefreshCw className={cn('w-3.5 h-3.5', busy && 'animate-spin')} />
        </button>
      )}
    </div>
  );
};
